import { useQuery, type QueryClient } from '@tanstack/react-query';
import { api } from './api';
import { qk } from './realtime-cache';
import { useRealtime } from './realtime';
import type { AdminRide, Commissions, Dashboard, FareConfig, Milestone, OnlineDriver, Page, Settings, SosRecord, Staff } from './types';

/** Poll as a fallback while the socket is down (docs/API.md §9); a slow safety net while it's up. */
const useLiveInterval = () => (useRealtime().connected ? 30_000 : 5_000);

export const useDashboard = () => {
  const interval = useLiveInterval();
  return useQuery({ queryKey: qk.dashboard, queryFn: () => api.get<Dashboard>('/admin/dashboard'), refetchInterval: interval * 2 });
};

export const useLiveRides = () => {
  const interval = useLiveInterval();
  return useQuery({ queryKey: qk.liveRides, queryFn: () => api.get<AdminRide[]>('/admin/rides/live'), refetchInterval: interval });
};

export const useOnlineDrivers = () => {
  const interval = useLiveInterval();
  return useQuery({
    queryKey: qk.liveDrivers,
    queryFn: () => api.get<OnlineDriver[]>('/admin/drivers/online'),
    refetchInterval: interval,
  });
};

/** SOS alerts that still need someone: OPEN and ACKNOWLEDGED. */
export const useActiveSos = () => {
  const interval = useLiveInterval();
  return useQuery({
    queryKey: qk.activeSos,
    queryFn: async () => {
      const [open, ack] = await Promise.all([
        api.get<Page<SosRecord>>('/admin/sos', { status: 'OPEN', pageSize: 50 }),
        api.get<Page<SosRecord>>('/admin/sos', { status: 'ACKNOWLEDGED', pageSize: 50 }),
      ]);
      return [...open.items, ...ack.items];
    },
    refetchInterval: interval,
  });
};

export const useFares = () => useQuery({ queryKey: qk.fares, queryFn: () => api.get<FareConfig[]>('/admin/fares') });
export const useSettings = () => useQuery({ queryKey: qk.settings, queryFn: () => api.get<Settings>('/admin/settings') });
export const useCommissions = () =>
  useQuery({ queryKey: qk.commissions, queryFn: () => api.get<Commissions>('/admin/commissions') });
export const useMilestones = () => useQuery({ queryKey: qk.milestones, queryFn: () => api.get<Milestone[]>('/admin/milestones') });
export const useStaff = () => useQuery({ queryKey: qk.staff, queryFn: () => api.get<Staff[]>('/admin/staff'), staleTime: 60_000 });

/** Staff id → name, for audit and SOS lines. */
export function useStaffNames() {
  const { data } = useStaff();
  return (id: string | null | undefined) => {
    if (!id) return 'System';
    return data?.find((s) => s.id === id)?.name ?? 'Staff';
  };
}

/** Show an SOS as taken straight away (the server confirms; a refetch follows either way). */
export function markSosTaken(qc: QueryClient, id: string, adminId: string | undefined) {
  const at = new Date().toISOString();
  const take = (s: SosRecord) => (s.id === id && s.status === 'OPEN' ? { ...s, status: 'ACKNOWLEDGED' as const, acknowledgedBy: adminId ?? null, acknowledgedAt: at } : s);
  qc.setQueryData<SosRecord[]>(qk.activeSos, (list) => list?.map(take));
  qc.setQueriesData<Page<SosRecord>>({ queryKey: [...qk.sos, 'list'] }, (p) => (p ? { ...p, items: p.items.map(take) } : p));
}
